import React, { useEffect } from 'react';
import SEOHead from '@/components/seo/SEOHead';
import { lazy, Suspense } from 'react';
import { Footer } from '@/components/layout/Footer';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

// Lazy load the HowItWorks component for better performance
const HowItWorksComponent = lazy(() => import('@/components/landing/HowItWorks'));

const howToStructuredData = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  "name": "How to Use Meduler: The Meduler 5-Step Adherence System",
  "description": "Set up Meduler and improve medication adherence in under 5 minutes. Add medications, set schedules, enable reminders, track doses, and review your adherence insights.",
  "totalTime": "PT5M",
  "estimatedCost": {
    "@type": "MonetaryAmount",
    "currency": "USD",
    "value": "0"
  },
  "tool": {
    "@type": "HowToTool",
    "name": "Meduler App"
  },
  "step": [
    {
      "@type": "HowToStep",
      "position": 1,
      "name": "Add your medications",
      "text": "Open Meduler and tap the '+' button. Enter each medication's name, dosage, and form. No account or sign-up is needed to start — your data stays on your device."
    },
    {
      "@type": "HowToStep",
      "position": 2,
      "name": "Set your schedule",
      "text": "Configure when you take each medication — morning, afternoon, evening, or custom times. Meduler supports daily, weekly, or interval-based schedules for complex regimens."
    },
    {
      "@type": "HowToStep",
      "position": 3,
      "name": "Enable reminders",
      "text": "Turn on push notifications so Meduler alerts you at the right time. Customize the reminder tone, timing, and snooze interval to match your daily routine."
    },
    {
      "@type": "HowToStep",
      "position": 4,
      "name": "Track adherence",
      "text": "Mark each dose as taken directly from your schedule view. Meduler records your history and calculates your adherence rate so you always know how you're doing."
    },
    {
      "@type": "HowToStep",
      "position": 5,
      "name": "Review insights",
      "text": "Check your weekly and monthly adherence charts to spot patterns, celebrate streaks, and share progress reports with your doctor or caregiver."
    }
  ]
};

const howItWorksFaqs = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "How long does it take to set up Meduler?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Most users add their first medication and set a reminder in under 60 seconds. No account or sign-up is required — just open the app and tap the '+' button to begin."
      }
    },
    {
      "@type": "Question",
      "name": "Does Meduler work without an internet connection?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. Meduler is offline-first. Your entire medication schedule, history, and reminders work without Wi-Fi or mobile data. All data is stored locally on your device."
      }
    },
    {
      "@type": "Question",
      "name": "Can a caregiver use Meduler to manage medications for someone else?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Absolutely. The free plan supports dual user profiles, so a caregiver can manage medications for a loved one alongside their own schedule. Meduler Plus adds advanced caregiver alerts and shared access features."
      }
    },
    {
      "@type": "Question",
      "name": "How does Meduler track medication adherence?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "When you mark a dose as taken, Meduler logs the time and calculates your adherence percentage. Weekly and monthly charts give you a visual history of your consistency, which you can share with your healthcare provider."
      }
    },
    {
      "@type": "Question",
      "name": "Is Meduler suitable for complex medication regimens?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. Meduler supports an unlimited number of medications with flexible scheduling — including daily, twice-daily, weekly, every-N-days, and custom time patterns — making it well-suited for managing multiple chronic conditions."
      }
    }
  ]
};

const combinedHowItWorksSchema = {
  "@context": "https://schema.org",
  "@graph": [howToStructuredData, howItWorksFaqs]
};

const HowItWorksPage: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  useEffect(() => {
    if (typeof window !== 'undefined' && 'gtag' in window) {
      (window as any).gtag('event', 'page_view', {
        page_title: 'How Meduler Works',
        page_path: window.location.pathname,
      });
    }
    
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.update().catch(() => {});
      });
    }
  }, []);

  const HowItWorksSkeleton = () => (
    <div className="py-16 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="h-12 w-40 bg-gray-200 rounded animate-pulse mb-6"></div>
        <div className="h-16 bg-gray-200 rounded animate-pulse mb-10 max-w-2xl"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white p-6 rounded-lg shadow-sm">
              <div className="h-12 w-12 rounded-full bg-gray-200 animate-pulse mb-4"></div>
              <div className="h-6 bg-gray-200 rounded animate-pulse mb-3 w-3/4"></div>
              <div className="h-20 bg-gray-200 rounded animate-pulse"></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <SEOHead
        title="How Meduler Works — 5-Step Setup Guide"
        description="Learn how to set up Meduler in under 5 minutes: add medications, set schedules, enable reminders, track doses, and review adherence insights."
        keywords="how to use meduler, medication app setup, medication reminder setup, medication adherence tracker, pill reminder guide"
        canonicalUrl="https://www.meduler.com/how-it-works"
        image="https://www.meduler.com/social/og-image.png"
        structuredData={combinedHowItWorksSchema}
      />

      <main className="min-h-screen bg-gradient-to-b from-white via-purple-50 to-white">
        {/* Hero Section — GEO template: question-format H1 + direct-answer paragraph */}
        <section className="pt-4 md:pt-6 pb-12 px-4 md:px-8">
          <motion.div 
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-block px-3 py-1 mb-4 rounded-full bg-purple-100 border border-purple-200">
              <p className="text-sm font-medium text-purple-700">Simple Process</p>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              How Does <span className="text-purple-600">Meduler</span> Work?
            </h1>
            {/* Direct-answer paragraph within first 100 words — GEO/AEO requirement */}
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-4">
              Meduler works in five steps: add your medications, set your schedule, enable reminders, track each dose as taken, and review your adherence insights. Most users are fully set up in under 5 minutes, with no account or sign-up required.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
              <a 
                href="/" 
                className="inline-flex items-center justify-center px-6 py-3 bg-purple-600 text-white font-medium rounded-lg shadow-sm hover:bg-purple-700 transition-colors"
              >
                Start for Free
              </a>
              <a 
                href="/features" 
                className="inline-flex items-center justify-center px-6 py-3 bg-white border border-purple-200 text-purple-600 font-medium rounded-lg shadow-sm hover:bg-purple-50 transition-colors"
              >
                View Features
              </a>
            </div>
          </motion.div>
        </section>

        {/* The Meduler 5-Step Adherence System — named framework section */}
        <section className="py-12 px-4 md:px-8 bg-white" aria-labelledby="system-heading">
          <div className="max-w-4xl mx-auto">
            <h2 id="system-heading" className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-3">
              The Meduler 5-Step Adherence System
            </h2>
            <p className="text-center text-gray-600 mb-10 max-w-2xl mx-auto">
              A structured approach to building consistent medication habits — whether you manage one prescription or a complex daily regimen.
            </p>
            <ol className="space-y-6">
              {[
                { step: 1, title: "Add your medications", desc: "Enter each medication's name, dosage, and form. No account needed — your data stays on your device and works offline." },
                { step: 2, title: "Set your schedule", desc: "Configure daily, weekly, or interval-based timing. Meduler handles simple once-daily pills and complex multi-drug regimens alike." },
                { step: 3, title: "Enable reminders", desc: "Turn on push notifications and customize the tone, timing, and snooze interval to fit your natural daily rhythm." },
                { step: 4, title: "Track adherence", desc: "Mark doses as taken from your schedule view. Meduler logs the exact time and calculates your running adherence rate." },
                { step: 5, title: "Review insights", desc: "Check weekly and monthly adherence charts, celebrate streaks, and share progress reports with your doctor or care team." },
              ].map(({ step, title, desc }) => (
                <li key={step} className="flex gap-5 p-5 rounded-xl bg-purple-50 border border-purple-100">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-purple-600 text-white font-bold flex items-center justify-center text-lg">{step}</div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Segment-specific advice */}
        <section className="py-12 px-4 md:px-8" aria-labelledby="segment-heading">
          <div className="max-w-4xl mx-auto">
            <h2 id="segment-heading" className="text-2xl font-bold text-center text-gray-900 mb-8">
              Meduler for Different Situations
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-6 rounded-xl bg-white border border-purple-100 shadow-sm">
                <h3 className="font-bold text-gray-900 mb-3">For chronic condition patients</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Managing multiple medications for conditions like diabetes, hypertension, or rheumatoid arthritis? Meduler's flexible scheduling supports complex regimens. According to the WHO, medication non-adherence accounts for roughly 50% of treatment failures for chronic conditions — Meduler's step-by-step system helps close that gap.
                </p>
              </div>
              <div className="p-6 rounded-xl bg-white border border-purple-100 shadow-sm">
                <h3 className="font-bold text-gray-900 mb-3">For caregivers</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  The free Starter plan includes dual user profiles so caregivers can manage a loved one's medications alongside their own. Meduler Plus adds caregiver alerts so you're notified if a dose is missed — even when you're not there.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Limitations section */}
        <section className="py-10 px-4 md:px-8 bg-gray-50 border-y border-gray-100" aria-labelledby="limitations-heading">
          <div className="max-w-3xl mx-auto">
            <h2 id="limitations-heading" className="text-xl font-bold text-gray-800 mb-4">When Meduler isn't the right fit</h2>
            <ul className="space-y-2 text-gray-600 text-sm list-disc list-inside">
              <li>Meduler does not check for drug interactions — always consult your pharmacist or prescriber.</li>
              <li>Meduler does not replace professional medical advice or act as a clinical tool.</li>
              <li>If you need complex clinical workflows or integrations with EHR systems, a dedicated pharmacy management platform may be more appropriate.</li>
            </ul>
          </div>
        </section>

        {/* How It Works Component — Lazy Loaded */}
        <motion.div 
          ref={ref}
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="w-full"
        >
          <Suspense fallback={<HowItWorksSkeleton />}>
            <HowItWorksComponent />
          </Suspense>
        </motion.div>

        {/* FAQ Section */}
        <section className="py-12 px-4 md:px-8" aria-labelledby="faq-how-heading">
          <div className="max-w-3xl mx-auto">
            <h2 id="faq-how-heading" className="text-2xl font-bold text-center text-gray-900 mb-8">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {[
                { q: "How long does it take to set up Meduler?", a: "Most users add their first medication and set a reminder in under 60 seconds. No account is required — just open the app and tap the '+' button." },
                { q: "Does Meduler work without an internet connection?", a: "Yes. Meduler is offline-first. Your schedule, history, and reminders all work without Wi-Fi or mobile data." },
                { q: "Can a caregiver use Meduler to manage medications for someone else?", a: "Absolutely. The free plan supports dual user profiles. Meduler Plus adds caregiver alerts and shared access features." },
                { q: "How does Meduler track medication adherence?", a: "Marking a dose as taken logs the exact time. Meduler then calculates your adherence percentage and displays weekly and monthly charts." },
                { q: "Is Meduler suitable for complex medication regimens?", a: "Yes. Meduler supports unlimited medications with daily, twice-daily, weekly, every-N-days, and custom time patterns." },
              ].map((faq, i) => (
                <details key={i} className="rounded-xl border border-purple-100 bg-white shadow-sm group">
                  <summary className="p-5 font-semibold text-gray-800 cursor-pointer list-none flex justify-between items-center">
                    {faq.q}
                    <span className="text-purple-400 group-open:rotate-180 transition-transform">▼</span>
                  </summary>
                  <div className="px-5 pb-5 text-gray-600 text-sm leading-relaxed">{faq.a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Plus Tier Promotion */}
        <section className="py-16 px-4 md:px-8 bg-gradient-to-r from-purple-600 to-violet-700 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Need more advanced features?</h2>
            <p className="text-xl mb-8 text-purple-100">
              Upgrade to Meduler Plus for additional caregiver tools, medication interaction checks, and detailed reports.
            </p>
            <a 
              href="/plus" 
              className="inline-flex items-center justify-center px-8 py-3 bg-white text-purple-600 font-medium rounded-lg shadow-lg hover:bg-purple-50 transition-colors"
            >
              Learn About Meduler Plus
            </a>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
};

export default HowItWorksPage;
